import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { completePenSwitchRender } from '@/analytics/core/penSwitchRenderTiming';
import {
    type RetryableQuery,
    type RetryProps,
    retryFailedQueries,
} from '@/components/retryFailedQueries';
import SomethingHappened from '@/components/SomethingHappened';
import LICENSES from '@/config/licenses';
import { usePermissions } from '@/contexts/auth';
import { useCage, useCageName, useCageTimezone } from '@/contexts/cage';
import useCageFilter, {
    DEFAULT_DAYPART_TAG,
    useCageScopedSearch,
} from '@/contexts/cage-filter-context';
import { usePenCohortFishGroup } from '@/contexts/pen-cohort-context';
import { useGetCageHealthOverview } from '@/domain/cage-health-overview';
import { useGetCageWelfareOverview } from '@/domain/cage-welfare-overview';
import { OVERVIEW_WEIGHT_UNIT_TAG, useGetCageStatus } from '@/domain/cages';
import { penSpeciesLabelKey, speciesTraits } from '@/domain/cages/speciesTraits';
import { useGetHealthQuality } from '@/domain/health-quality';
import { fullSizeImageMapper, useGetWelfareImages } from '@/domain/images';
import { selectSnapshotMeasurementSource, useGetCageLast200Lice } from '@/domain/lice';
import { useImageState } from '@/hooks/image-state';
import { useIsSmolt } from '@/hooks/useCageTypeFlags';
import { useCageOverview } from '@/routes/Client/Cage/components/cage-overview-context';
import type { CagePageInterface } from '@/routes/Client/Cage/utils/cage-pages-interface';
import { isBwBioscopeVersion, isColorBioscopeVersion } from '@/utils/bioscopeVersionHelpers';
import { isChileanLiceType } from '@/utils/locationTypeHelpers';
import { createCageUrl } from '@/utils/urlGenerator';
import { MERGED_WELFARE_IMAGES_TYPE } from '@/utils/welfareImageHelpers';

import CageDashboardView from './CageDashboardView';

const CAGE_STATUS_STALE_TIME = 60 * 1000;
const OVERVIEW_STALE_TIME = 5 * 60 * 1000;

/** Try again for a card whose request failed, or null while it has not. */
function loadErrorOf(query: RetryableQuery): RetryProps | null {
    return query.error ? retryFailedQueries(query) : null;
}

/** This page has no source toggle; the calibrated family is what it reports. */
const LICE_MEASUREMENT_SOURCE = 'legacy';

export default function CageDashboardContainer(props: CagePageInterface) {
    const { clientId, locationId, cageId } = props;
    const { t } = useTranslation();
    const { isOptoScaleAdmin } = usePermissions();
    const { bioscope, loading: isCageContextLoading, client, hasCageLicense, stock } = useCage();
    const cageName = useCageName();
    const timezone = useCageTimezone();
    const isSmolt = useIsSmolt();
    const {
        loading: isOverviewLoading,
        error: overviewError,
        isFetching: isOverviewFetching,
        refetch: refetchOverview,
        data: overview,
        daypartTag = DEFAULT_DAYPART_TAG,
    } = useCageOverview();

    useEffect(() => {
        if (isOverviewLoading || overviewError) {
            return;
        }
        completePenSwitchRender(cageId);
    }, [cageId, isOverviewLoading, overviewError]);

    const showWelfareOverview = !isCageContextLoading && isBwBioscopeVersion(bioscope?.version);
    const showHealthOverview = !isCageContextLoading && isColorBioscopeVersion(bioscope?.version);

    const hasHealthLicense = hasCageLicense(LICENSES.FISH_HEALTH.MIN);
    const hasQualityLicense = hasCageLicense(LICENSES.BIOMASS_WITH_QUALITY);
    const hasBehaviorLicense = hasCageLicense(LICENSES.BEHAVIOR);
    const hasSpeedAccess = hasCageLicense(LICENSES.FISH_SPEED) || hasBehaviorLicense;

    const cageStatus = useGetCageStatus(cageId, { staleTime: CAGE_STATUS_STALE_TIME });

    const { selectedFishGroupId } = useCageFilter();
    // The module pages answer for the same fish group (`fg`) as this dashboard.
    const moduleSearch = useCageScopedSearch();
    const welfareOverview = useGetCageWelfareOverview(
        cageId,
        { fishGroupId: selectedFishGroupId },
        {
            enabled: showWelfareOverview,
            staleTime: OVERVIEW_STALE_TIME,
            gcTime: OVERVIEW_STALE_TIME,
        }
    );

    const healthOverview = useGetCageHealthOverview(
        cageId,
        { fishGroupId: selectedFishGroupId },
        {
            enabled: showHealthOverview,
            staleTime: OVERVIEW_STALE_TIME,
            gcTime: OVERVIEW_STALE_TIME,
        }
    );

    // The estimate's model is fitted for salmon. The species is the pen's, and it is only known
    // once the context has resolved; `showWelfareOverview`/`showHealthOverview` already wait for
    // that, so no estimate is requested or drawn for a pen whose species is still unknown.
    const hasDowngradeEstimate = speciesTraits(
        stock?.fishType?.shortName
    ).dashboardDowngradeEstimate;
    const showQuality =
        !isSmolt &&
        hasQualityLicense &&
        hasDowngradeEstimate &&
        (showWelfareOverview || showHealthOverview);
    const healthQuality = useGetHealthQuality(
        cageId,
        { fishGroupId: selectedFishGroupId },
        {
            enabled: showQuality && showHealthOverview,
            staleTime: OVERVIEW_STALE_TIME,
            gcTime: OVERVIEW_STALE_TIME,
        }
    );

    // Gated again here, not only by `enabled`: a disabled query still serves whatever another
    // page cached for this cage, and those cards do not apply to this bioscope.
    const welfare = showWelfareOverview ? welfareOverview.data : undefined;
    const isWelfareLoading =
        isCageContextLoading || (showWelfareOverview && welfareOverview.isLoading);

    const last200 = useGetCageLast200Lice(
        cageId,
        { fishGroupId: selectedFishGroupId },
        { staleTime: OVERVIEW_STALE_TIME }
    );

    const imagesFishGroup = usePenCohortFishGroup();
    const detections = useGetWelfareImages(
        cageId,
        {
            type: MERGED_WELFARE_IMAGES_TYPE,
            numberOfImages: '1',
        },
        { fishGroup: imagesFishGroup }
    );
    const detectionMapper = useMemo(() => fullSizeImageMapper(cageId), [cageId]);
    const detectionImageState = useImageState({
        loading: detections.isLoading && !detections.error,
        data: detections.data,
        mapper: detectionMapper,
        queryParam: 'imageId',
        timezone,
    });

    // The page's own title goes with the view, so the error state stands in as its h1.
    if (overviewError) {
        return (
            <SomethingHappened
                error={overviewError}
                headingLevel="h1"
                onRetry={refetchOverview}
                isRetrying={isOverviewFetching}
            />
        );
    }

    const cageUrl = createCageUrl(clientId, locationId, cageId);
    // The species in THIS pen, which may differ from the location's default (WB-2207). Salmon
    // gets no label — it is what a reader assumes — and the decision of which species those are
    // sits in `penSpeciesLabelKey` rather than as a comparison here.
    const speciesLabelKey = penSpeciesLabelKey(stock?.fishType);

    // BW reuses the welfare overview this page already loads. Color fetches the health
    // quality snapshot. Only one source applies, so the view gets a resolved group.
    const quality = showHealthOverview
        ? {
              show: showQuality,
              downgradeRate: healthQuality.data?.downgradeRate ?? undefined,
              lastMeasurement: healthQuality.data?.day ?? null,
              isLoading: isCageContextLoading || (showQuality && healthQuality.isLoading),
              loadError: loadErrorOf(healthQuality),
          }
        : {
              show: showQuality,
              downgradeRate: welfare?.welfare?.downgradeRate,
              lastMeasurement: welfare?.welfare?.day ?? null,
              isLoading: isWelfareLoading,
              loadError: showWelfareOverview ? loadErrorOf(welfareOverview) : null,
          };

    return (
        <CageDashboardView
            title={cageName}
            speciesLabel={
                speciesLabelKey
                    ? t(speciesLabelKey.key, { defaultValue: speciesLabelKey.defaultValue })
                    : undefined
            }
            isSmolt={isSmolt}
            isCageContextLoading={isCageContextLoading}
            urls={{
                cage: cageUrl,
                weight: `${cageUrl}/weight${moduleSearch}`,
                lice: `${cageUrl}/lice${moduleSearch}`,
                welfare: `${cageUrl}/welfare${moduleSearch}`,
                wounds: `${cageUrl}/welfare/wounds${moduleSearch}`,
                swimSpeed: hasBehaviorLicense
                    ? `${cageUrl}/behavior/swim-speed${moduleSearch}`
                    : `${cageUrl}/welfare/speed${moduleSearch}`,
                health: `${cageUrl}/health${moduleSearch}`,
                images: `${cageUrl}/dashboard/images${moduleSearch}`,
            }}
            overview={overview}
            isOverviewLoading={isOverviewLoading}
            weightUnitTag={OVERVIEW_WEIGHT_UNIT_TAG}
            daypartTag={daypartTag}
            lice={{
                snapshot: last200.data
                    ? selectSnapshotMeasurementSource(last200.data, LICE_MEASUREMENT_SOURCE)
                    : null,
                isLoading: last200.isLoading,
                hasLicense: hasCageLicense(LICENSES.LICE_COUNT.MIN),
                isChilean: isChileanLiceType(stock?.liceType),
                loadError: loadErrorOf(last200),
            }}
            welfare={{
                show: hasHealthLicense && showWelfareOverview,
                data: welfare?.welfare,
                swimSpeed: welfare?.swimSpeed,
                hasSpeedAccess,
                isLoading: isWelfareLoading,
                loadError: showWelfareOverview ? loadErrorOf(welfareOverview) : null,
            }}
            health={{
                show: hasHealthLicense && showHealthOverview,
                data: healthOverview.data ?? undefined,
                isLoading: healthOverview.isLoading,
                loadError: showHealthOverview ? loadErrorOf(healthOverview) : null,
            }}
            quality={quality}
            detections={{
                images: detectionImageState.images,
                isLoading: detectionImageState.loading,
                loadError: loadErrorOf(detections),
            }}
            cageStatus={{
                data: cageStatus.data,
                isLoading: cageStatus.isLoading,
                isOptoScaleAdmin,
                showInspectionLink: client?.featureFlags?.includes('BIOSCOPE_INSPECTION') ?? false,
                loadError: loadErrorOf(cageStatus),
            }}
        />
    );
}
